import type { LanguageSupport, StreamParser } from '@codemirror/language';

/**
 * Every language the editor can highlight.
 *
 * Modes are dynamic imports, so the initial bundle carries none of them — a page showing many
 * small snippets would otherwise pay for every grammar up front. `extensions` drives guessing
 * from a fragment's filename; the user can always override.
 */
export interface LanguageDefinition {
  /** Stored on the fragment. Lower-case, matches the API's validation. */
  id: string;
  label: string;
  extensions: string[];
  load: () => Promise<LanguageSupport | StreamParser<unknown> | undefined>;
}

async function legacy(name: keyof typeof legacyModes): Promise<StreamParser<unknown>> {
  return legacyModes[name]();
}

// Grammars without a first-class CodeMirror 6 package still have a stream parser.
const legacyModes = {
  shell: async () => (await import('@codemirror/legacy-modes/mode/shell')).shell,
  powershell: async () => (await import('@codemirror/legacy-modes/mode/powershell')).powerShell,
  ruby: async () => (await import('@codemirror/legacy-modes/mode/ruby')).ruby,
  lua: async () => (await import('@codemirror/legacy-modes/mode/lua')).lua,
  perl: async () => (await import('@codemirror/legacy-modes/mode/perl')).perl,
  dockerfile: async () => (await import('@codemirror/legacy-modes/mode/dockerfile')).dockerFile,
  toml: async () => (await import('@codemirror/legacy-modes/mode/toml')).toml,
  ini: async () => (await import('@codemirror/legacy-modes/mode/properties')).properties,
  diff: async () => (await import('@codemirror/legacy-modes/mode/diff')).diff,
  swift: async () => (await import('@codemirror/legacy-modes/mode/swift')).swift,
  haskell: async () => (await import('@codemirror/legacy-modes/mode/haskell')).haskell,
  clojure: async () => (await import('@codemirror/legacy-modes/mode/clojure')).clojure,
  r: async () => (await import('@codemirror/legacy-modes/mode/r')).r,
  nginx: async () => (await import('@codemirror/legacy-modes/mode/nginx')).nginx,
} as const;

export const languages: LanguageDefinition[] = [
  {
    id: 'javascript',
    label: 'JavaScript',
    extensions: ['js', 'mjs', 'cjs', 'jsx'],
    load: async () => (await import('@codemirror/lang-javascript')).javascript({ jsx: true }),
  },
  {
    id: 'typescript',
    label: 'TypeScript',
    extensions: ['ts', 'mts', 'cts', 'tsx'],
    load: async () =>
      (await import('@codemirror/lang-javascript')).javascript({ typescript: true, jsx: true }),
  },
  {
    id: 'csharp',
    label: 'C#',
    extensions: ['cs', 'csx'],
    load: async () => (await import('@replit/codemirror-lang-csharp')).csharp(),
  },
  {
    id: 'python',
    label: 'Python',
    extensions: ['py', 'pyw'],
    load: async () => (await import('@codemirror/lang-python')).python(),
  },
  {
    id: 'sql',
    label: 'SQL',
    extensions: ['sql'],
    load: async () => (await import('@codemirror/lang-sql')).sql(),
  },
  {
    id: 'json',
    label: 'JSON',
    extensions: ['json', 'jsonc'],
    load: async () => (await import('@codemirror/lang-json')).json(),
  },
  {
    id: 'yaml',
    label: 'YAML',
    extensions: ['yaml', 'yml'],
    load: async () => (await import('@codemirror/lang-yaml')).yaml(),
  },
  {
    id: 'html',
    label: 'HTML',
    extensions: ['html', 'htm'],
    load: async () => (await import('@codemirror/lang-html')).html(),
  },
  {
    id: 'css',
    label: 'CSS',
    extensions: ['css'],
    load: async () => (await import('@codemirror/lang-css')).css(),
  },
  {
    id: 'markdown',
    label: 'Markdown',
    extensions: ['md', 'markdown'],
    load: async () => (await import('@codemirror/lang-markdown')).markdown(),
  },
  {
    id: 'rust',
    label: 'Rust',
    extensions: ['rs'],
    load: async () => (await import('@codemirror/lang-rust')).rust(),
  },
  {
    id: 'go',
    label: 'Go',
    extensions: ['go'],
    load: async () => (await import('@codemirror/lang-go')).go(),
  },
  {
    id: 'java',
    label: 'Java',
    extensions: ['java'],
    load: async () => (await import('@codemirror/lang-java')).java(),
  },
  {
    id: 'cpp',
    label: 'C / C++',
    extensions: ['c', 'h', 'cpp', 'cc', 'hpp'],
    load: async () => (await import('@codemirror/lang-cpp')).cpp(),
  },
  {
    id: 'php',
    label: 'PHP',
    extensions: ['php'],
    load: async () => (await import('@codemirror/lang-php')).php(),
  },
  {
    id: 'xml',
    label: 'XML',
    extensions: ['xml', 'xsd', 'xsl', 'csproj', 'props', 'targets'],
    load: async () => (await import('@codemirror/lang-xml')).xml(),
  },
  { id: 'shell', label: 'Shell / Bash', extensions: ['sh', 'bash', 'zsh'], load: () => legacy('shell') },
  { id: 'powershell', label: 'PowerShell', extensions: ['ps1', 'psm1'], load: () => legacy('powershell') },
  { id: 'ruby', label: 'Ruby', extensions: ['rb'], load: () => legacy('ruby') },
  { id: 'lua', label: 'Lua', extensions: ['lua'], load: () => legacy('lua') },
  { id: 'perl', label: 'Perl', extensions: ['pl', 'pm'], load: () => legacy('perl') },
  { id: 'dockerfile', label: 'Dockerfile', extensions: ['dockerfile'], load: () => legacy('dockerfile') },
  { id: 'toml', label: 'TOML', extensions: ['toml'], load: () => legacy('toml') },
  { id: 'ini', label: 'INI / properties', extensions: ['ini', 'conf', 'properties'], load: () => legacy('ini') },
  { id: 'diff', label: 'Diff / patch', extensions: ['diff', 'patch'], load: () => legacy('diff') },
  { id: 'swift', label: 'Swift', extensions: ['swift'], load: () => legacy('swift') },
  { id: 'haskell', label: 'Haskell', extensions: ['hs'], load: () => legacy('haskell') },
  { id: 'clojure', label: 'Clojure', extensions: ['clj', 'cljs'], load: () => legacy('clojure') },
  { id: 'r', label: 'R', extensions: ['r'], load: () => legacy('r') },
  { id: 'nginx', label: 'nginx config', extensions: ['nginx'], load: () => legacy('nginx') },
  {
    id: 'text',
    label: 'Plain text',
    extensions: ['txt', 'log'],
    // No mode at all. This used to load the JSON grammar, which highlighted plain text as broken
    // JSON — and "text" is the language every fragment starts in, so it was the common case.
    load: async () => undefined,
  },
];

const byId = new Map(languages.map((language) => [language.id, language]));

const byExtension = new Map(
  languages.flatMap((language) =>
    language.extensions.map((extension) => [extension, language] as const),
  ),
);

export function findLanguage(id: string): LanguageDefinition | undefined {
  return byId.get(id.toLowerCase());
}

export function languageLabel(id: string): string {
  return byId.get(id.toLowerCase())?.label ?? id;
}

/**
 * Guesses a language from a filename. Returns undefined rather than a default, so a caller can
 * tell "I recognised this" apart from "I gave up" — the user's own choice must never be
 * overwritten by a guess.
 */
export function guessLanguageFromName(name: string): LanguageDefinition | undefined {
  const trimmed = name.trim().toLowerCase();
  if (!trimmed) {
    return undefined;
  }

  // Dockerfile and Makefile carry the type in the whole name, not an extension.
  if (trimmed === 'dockerfile' || trimmed.startsWith('dockerfile.')) {
    return byId.get('dockerfile');
  }

  const extension = trimmed.includes('.') ? trimmed.slice(trimmed.lastIndexOf('.') + 1) : trimmed;

  return byExtension.get(extension);
}

/** Case-insensitive substring match over id and label, for the picker's filter box. */
export function searchLanguages(query: string): LanguageDefinition[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return languages;
  }

  return languages.filter(
    (language) =>
      language.id.includes(needle) ||
      language.label.toLowerCase().includes(needle) ||
      language.extensions.some((extension) => extension === needle),
  );
}
